import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useNavigate } from 'react-router-dom'
import {
  createWishlistItem,
  deleteWishlistItem,
  loadWishlistItemsForUser,
} from '../api/wishlistApi'
import {
  clearShopUserValidationCache,
  getAssetIdFromProduct,
  resolveShopUserId,
} from '../api/shopAuth'

const WishlistContext = createContext(null)

function getProductId(product) {
  const id = getAssetIdFromProduct(product)
  return id == null ? '' : String(id)
}

export function WishlistProvider({ children }) {
  const navigate = useNavigate()
  const [userId, setUserId] = useState(null)
  const [authReady, setAuthReady] = useState(false)
  const [items, setItems] = useState([])
  const [isSyncing, setIsSyncing] = useState(false)
  const itemsRef = useRef(items)
  itemsRef.current = items

  const refreshAuth = useCallback(async () => {
    clearShopUserValidationCache()
    setAuthReady(false)
    try {
      const id = await resolveShopUserId()
      setUserId(id)
    } finally {
      setAuthReady(true)
    }
  }, [])

  useEffect(() => {
    refreshAuth()
    const onAuth = () => {
      refreshAuth()
    }
    window.addEventListener('auth-changed', onAuth)
    window.addEventListener('storage', onAuth)
    return () => {
      window.removeEventListener('auth-changed', onAuth)
      window.removeEventListener('storage', onAuth)
    }
  }, [refreshAuth])

  useEffect(() => {
    if (!authReady) return undefined

    if (userId == null) {
      setItems([])
      return undefined
    }

    let cancelled = false
    setIsSyncing(true)
    ;(async () => {
      try {
        const loaded = await loadWishlistItemsForUser(userId)
        if (!cancelled) setItems(loaded)
      } catch {
        if (!cancelled) setItems([])
      } finally {
        if (!cancelled) setIsSyncing(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [authReady, userId])

  const requireLogin = useCallback(() => {
    if (userId == null) {
      navigate('/login', {
        state: {
          from: window.location.pathname + window.location.search,
          message: 'Please login to manage your wishlist',
        },
      })
      return false
    }
    return true
  }, [navigate, userId])

  const wishlistedIds = useMemo(
    () => new Set(items.map((item) => getProductId(item.product))),
    [items]
  )

  const isWishlisted = useCallback(
    (productId) => wishlistedIds.has(String(productId)),
    [wishlistedIds]
  )

  const toggleWishlist = useCallback(
    async (product) => {
      if (!authReady) return { ok: false, reason: 'loading' }
      if (!requireLogin()) return { ok: false, reason: 'login' }

      const productId = getProductId(product)
      const assetId = getAssetIdFromProduct(product)
      if (!productId || assetId == null) {
        return { ok: false, reason: 'invalid', message: 'Invalid product' }
      }

      const existing = itemsRef.current.find(
        (item) => getProductId(item.product) === productId
      )

      if (existing) {
        setItems((prev) =>
          prev.filter((item) => getProductId(item.product) !== productId)
        )
        try {
          if (existing.wishlistId > 0) {
            await deleteWishlistItem(existing.wishlistId)
          }
          return { ok: true, removed: true }
        } catch (error) {
          setItems((prev) => [...prev, existing])
          return {
            ok: false,
            reason: 'error',
            message: error.message || 'Failed to update wishlist',
          }
        }
      }

      const optimistic = {
        product,
        wishlistId: -assetId,
      }
      setItems((prev) => [...prev, optimistic])

      try {
        const created = await createWishlistItem({
          user_id: userId,
          asset_id: assetId,
        })
        setItems((prev) =>
          prev.map((item) =>
            getProductId(item.product) === productId
              ? { product, wishlistId: Number(created.wishlist_id) }
              : item
          )
        )
        return { ok: true, added: true }
      } catch (error) {
        try {
          const refreshed = await loadWishlistItemsForUser(userId)
          setItems(refreshed)
          if (
            refreshed.some((item) => getProductId(item.product) === productId)
          ) {
            return { ok: true, added: true }
          }
        } catch {
          setItems((prev) =>
            prev.filter((item) => getProductId(item.product) !== productId)
          )
        }
        return {
          ok: false,
          reason: 'error',
          message: error.message || 'Failed to update wishlist',
        }
      }
    },
    [authReady, requireLogin, userId]
  )

  const removeFromWishlist = useCallback(
    async (productId) => {
      if (!requireLogin()) return { ok: false, reason: 'login' }
      const id = String(productId)
      const existing = itemsRef.current.find(
        (item) => getProductId(item.product) === id
      )
      if (!existing) return { ok: true }

      setItems((prev) =>
        prev.filter((item) => getProductId(item.product) !== id)
      )
      try {
        if (existing.wishlistId > 0) {
          await deleteWishlistItem(existing.wishlistId)
        }
        return { ok: true }
      } catch (error) {
        setItems((prev) => [...prev, existing])
        return {
          ok: false,
          message: error.message || 'Failed to remove from wishlist',
        }
      }
    },
    [requireLogin]
  )

  const value = useMemo(
    () => ({
      items,
      wishlistCount: items.length,
      isSyncing: isSyncing || !authReady,
      isAuthenticated: userId != null,
      isWishlisted,
      toggleWishlist,
      removeFromWishlist,
    }),
    [
      items,
      isSyncing,
      authReady,
      userId,
      isWishlisted,
      toggleWishlist,
      removeFromWishlist,
    ]
  )

  return (
    <WishlistContext.Provider value={value}>{children}</WishlistContext.Provider>
  )
}

export function useWishlist() {
  const context = useContext(WishlistContext)
  if (!context) {
    throw new Error('useWishlist must be used within a WishlistProvider')
  }
  return context
}
